'use client';

/**
 * The way between the app's surfaces.
 *
 * Every screen had a title bar and no links, so a learner who reached /train could not get to the
 * dashboard, the map, or the catalog from anywhere in the product — the only exit was the browser's
 * back button. The founder hit it on his first real session (2026-08-26).
 *
 * One component rather than a copy per screen: the set of places a learner can go is a property of
 * the app, not of whichever screen they happen to be on, and the last thing this navigation needs is
 * to differ subtly between them.
 */
import Link from 'next/link';
import { useEffect, useState } from 'react';

const DESTINATIONS = [
  { href: '/train', label: 'Train' },
  { href: '/map', label: 'Map' },
  { href: '/home', label: 'Progress' },
  { href: '/catalog', label: 'Tracks' },
] as const;

export default function ConsoleNav() {
  // The current path is read from the document rather than through usePathname: the highlight is a
  // nicety, and taking a router dependency for it would put one on every screen that renders a
  // header — nineteen test files mock next/navigation and none of them owe this component a stub.
  const [pathname, setPathname] = useState<string | null>(null);
  useEffect(() => setPathname(window.location.pathname), []);

  return (
    <nav className="console-links" aria-label="Sections">
      {DESTINATIONS.map((destination) => {
        const current = pathname === destination.href;
        return (
          <Link
            key={destination.href}
            href={destination.href}
            className="console-link"
            // Marks the page you are on for a screen reader as well as visually, so the nav answers
            // "where am I" and not only "where can I go".
            aria-current={current ? 'page' : undefined}
            data-current={current ? 'true' : undefined}
          >
            {destination.label}
          </Link>
        );
      })}
    </nav>
  );
}
